import React from "react";
import { CheckCircle2, Circle, Globe2, RefreshCw, Upload } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { Textarea } from "@components/ui/textarea";
import {
  useLocalisationAdminOverviewQuery,
  useLocalisationEditorQuery,
  useQueueLocalisationTranslationMutation,
  useUpdateLocalisationTranslationMutation,
} from "generated/hooks";
import { toast } from "sonner";
import { cn } from "~/lib/utils";

interface Language {
  code: string;
  label: string;
}

interface TranslationRow {
  id: string;
  key: string;
  english: string;
  section: string;
  label: string;
  text: string;
  approved: boolean;
  isFallback: boolean;
  isTranslating: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isEditing: boolean;
}

interface SectionGroup {
  section: string;
  order: number;
  rows: TranslationRow[];
  hasUnapproved: boolean;
  approvedCount: number;
  totalCount: number;
  approvedPercent: number;
}

const LANGUAGE_SWITCH_DELAY_MS = 250;

const languageDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "language" })
    : null;
const regionDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const getLocaleParts = (locale: string): Language => {
  const [languageCode = locale] = locale.split("-");
  const languageName = languageDisplayNames?.of(languageCode) ?? locale;
  const region = locale.split("-").slice(1).find(Boolean);
  const regionName = region ? regionDisplayNames?.of(region) ?? region : null;

  return {
    code: locale,
    label: regionName ? `${languageName} (${regionName})` : languageName,
  };
};

type EditorTranslationRow = {
  key: string;
  baseValue: string;
  targetValue?: string | null;
  isFallback: boolean;
  isApproved: boolean;
  isTranslating: boolean;
};

export const buildTranslationRowId = (
  fileName: string,
  locale: string,
  key: string
) => `${fileName}::${locale}::${key}`;

const buildRowsFromEditor = (
  fileName: string,
  locale: string,
  rows: EditorTranslationRow[]
): TranslationRow[] =>
  rows.map((row) => {
    const keyParts = row.key.split(".");

    return {
      id: buildTranslationRowId(fileName, locale, row.key),
      key: row.key,
      english: row.baseValue,
      section: (keyParts[0] ?? row.key).toUpperCase(),
      label: keyParts[keyParts.length - 1] ?? row.key,
      text: row.targetValue ?? "",
      approved: row.isApproved,
      isFallback: row.isFallback,
      isTranslating: row.isTranslating,
      isDirty: false,
      isSaving: false,
      isEditing: false,
    };
  });

export const stageTranslationRow = (
  row: TranslationRow,
  rowId: string,
  value: string,
  isApproved: boolean
): TranslationRow =>
  row.id === rowId
    ? {
        ...row,
        text: value,
        approved: isApproved,
        isDirty: true,
      }
    : row;

const LocalisationLoadingState = () => (
  <Card className="overflow-hidden border-border/60 bg-card/70">
    <CardHeader className="border-b border-border/60">
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-4 w-20" />
      </div>
    </CardHeader>
    <CardContent className="px-0">
      <div className="space-y-3 px-6 py-5">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="grid gap-3 md:grid-cols-[160px_1fr_1.2fr]"
          >
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const SectionProgressIndicator = ({
  approvedPercent,
}: {
  approvedPercent: number;
}) => {
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const dashOffset =
    circumference - (Math.max(0, Math.min(approvedPercent, 100)) / 100) * circumference;

  return (
    <div className="flex items-center gap-2">
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="size-5 -rotate-90"
      >
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="rgb(71 85 105 / 0.2)"
          strokeWidth="3"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="rgb(16 185 129)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span className="text-[11px] font-medium tracking-[0.08em] text-muted-foreground">
        {approvedPercent}% approved
      </span>
    </div>
  );
};

const SectionHeaderRow = React.memo(
  ({
    section,
    approvedPercent,
  }: {
    section: string;
    approvedPercent: number;
  }) => (
    <TableRow className="border-b border-border/50 bg-muted/15 hover:bg-muted/15">
      <TableCell colSpan={3} className="px-6 py-3 whitespace-normal">
        <div className="flex items-center justify-between gap-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {section}
          </div>
          <SectionProgressIndicator approvedPercent={approvedPercent} />
        </div>
      </TableCell>
    </TableRow>
  )
);
SectionHeaderRow.displayName = "SectionHeaderRow";

interface TranslationTableRowProps {
  row: TranslationRow;
  isBusy: boolean;
  isRegenerating: boolean;
  onClearApproval: (rowId: string) => void;
  onSchedulePersist: (
    rowId: string,
    rowKey: string,
    value: string,
    isApproved: boolean
  ) => void;
  onPersistNow: (
    rowId: string,
    rowKey: string,
    value: string,
    isApproved: boolean
  ) => void;
  onApprovedToggle: (
    rowId: string,
    rowKey: string,
    value: string,
    nextApproved: boolean
  ) => void;
  onOpenRegenerateDialog: (row: TranslationRow) => void;
}

const TranslationTableRow = React.memo(
  ({
    row,
    isBusy,
    isRegenerating,
    onClearApproval,
    onSchedulePersist,
    onPersistNow,
    onApprovedToggle,
    onOpenRegenerateDialog,
  }: TranslationTableRowProps) => {
    const [draftValue, setDraftValue] = React.useState(row.text);
    const [isEditing, setIsEditing] = React.useState(false);
    const isApproved = row.approved;

    React.useEffect(() => {
      if (!isEditing) {
        setDraftValue(row.text);
      }
    }, [isEditing, row.text]);

    const handleChange = (value: string) => {
      setDraftValue(value);
      setIsEditing(true);

      if (row.approved) {
        onClearApproval(row.id);
      }

      onSchedulePersist(row.id, row.key, value, false);
    };

    const handleBlur = () => {
      setIsEditing(false);

      if (draftValue === row.text && !row.isSaving) {
        return;
      }

      onPersistNow(row.id, row.key, draftValue, row.approved);
    };

    return (
      <TableRow className="border-b border-border/40 hover:bg-transparent">
        <TableCell className="w-[20%] px-6 py-4 text-sm text-muted-foreground whitespace-normal break-words align-top">
          {row.label}
        </TableCell>
        <TableCell className="w-[40%] px-6 py-4 text-sm text-foreground/85 whitespace-pre-wrap break-words align-top">
          {row.english}
        </TableCell>
        <TableCell className="w-[40%] px-6 py-4 align-top">
          <div className="flex min-w-0 items-center gap-2">
            <Input
              value={draftValue}
              onChange={(event) => handleChange(event.target.value)}
              onFocus={() => setIsEditing(true)}
              onBlur={handleBlur}
              disabled={isBusy || isRegenerating || (isApproved && !isEditing)}
              className={cn(
                "h-10 min-w-0 border-border/70 bg-muted/10",
                isApproved && "border-emerald-500/30 bg-emerald-500/5"
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() =>
                onApprovedToggle(row.id, row.key, draftValue, !isApproved)
              }
              disabled={
                isBusy || isRegenerating || row.isSaving || !draftValue.trim()
              }
              aria-label={
                isApproved ? "Unapprove translation" : "Approve translation"
              }
              className={cn(
                "rounded-full text-muted-foreground",
                isApproved && "text-emerald-500"
              )}
            >
              {isApproved ? (
                <CheckCircle2 className="size-5" />
              ) : (
                <Circle className="size-5" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => onOpenRegenerateDialog(row)}
              disabled={isBusy || isRegenerating || row.isSaving}
              aria-label="Regenerate translation"
              className="rounded-full text-muted-foreground"
            >
              {isRegenerating ? (
                <Spinner className="size-4" />
              ) : (
                <RefreshCw className="size-4" />
              )}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }
);
TranslationTableRow.displayName = "TranslationTableRow";

const LocalisationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = React.useState<TranslationRow[]>([]);
  const [isSwitchingLanguage, setIsSwitchingLanguage] = React.useState(false);
  const [regenerateOpen, setRegenerateOpen] = React.useState(false);
  const [regenerateRowId, setRegenerateRowId] = React.useState<string | null>(
    null
  );
  const [regenerateContext, setRegenerateContext] = React.useState("");
  const [regeneratingRowId, setRegeneratingRowId] = React.useState<
    string | null
  >(null);
  const languageTimerRef = React.useRef<number | null>(null);
  const saveTimersRef = React.useRef<Record<string, number>>({});

  const selectedFileName = searchParams.get("file");
  const selectedLanguage = searchParams.get("locale");

  const {
    data: overviewData,
    loading: overviewLoading,
    error: overviewError,
  } = useLocalisationAdminOverviewQuery();

  const files = overviewData?.localisationAdminOverview.files ?? [];
  const selectedFile =
    files.find((file) => file.name === selectedFileName) ?? null;
  const languages = React.useMemo(
    () => (selectedFile?.targetLocales ?? []).map(getLocaleParts),
    [selectedFile?.targetLocales]
  );

  const {
    data: editorData,
    loading: editorLoading,
    error: editorError,
    refetch: refetchEditor,
  } = useLocalisationEditorQuery({
    variables:
      selectedFileName && selectedLanguage
        ? { name: selectedFileName, locale: selectedLanguage }
        : undefined,
    skip: !selectedFileName || !selectedLanguage,
  });

  React.useEffect(() => {
    return () => {
      if (languageTimerRef.current) {
        window.clearTimeout(languageTimerRef.current);
      }
    };
  }, []);

  const [queueLocalisationTranslation] =
    useQueueLocalisationTranslationMutation();
  const [updateLocalisationTranslation] =
    useUpdateLocalisationTranslationMutation();

  React.useEffect(() => {
    return () => {
      for (const timer of Object.values(saveTimersRef.current)) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  React.useEffect(() => {
    if (overviewLoading) {
      return;
    }

    if (files.length === 0) {
      if (selectedFileName || selectedLanguage) {
        React.startTransition(() => {
          setSearchParams(new URLSearchParams(), { replace: true });
        });
      }
      return;
    }

    const matchedFile = files.find((file) => file.name === selectedFileName);
    if (!matchedFile) {
      const params = new URLSearchParams();
      params.set("file", files[0].name);

      const firstLocale = files[0].targetLocales[0];
      if (firstLocale) {
        params.set("locale", firstLocale);
      }

      React.startTransition(() => {
        setSearchParams(params, { replace: true });
      });
      return;
    }

    if (
      selectedLanguage &&
      !matchedFile.targetLocales.includes(selectedLanguage)
    ) {
      const params = new URLSearchParams();
      params.set("file", matchedFile.name);

      const firstLocale = matchedFile.targetLocales[0];
      if (firstLocale) {
        params.set("locale", firstLocale);
      }

      React.startTransition(() => {
        setSearchParams(params, { replace: true });
      });
      return;
    }

    if (!selectedLanguage && matchedFile.targetLocales[0]) {
      const params = new URLSearchParams();
      params.set("file", matchedFile.name);
      params.set("locale", matchedFile.targetLocales[0]);

      React.startTransition(() => {
        setSearchParams(params, { replace: true });
      });
    }
  }, [
    files,
    overviewLoading,
    selectedFileName,
    selectedLanguage,
    setSearchParams,
  ]);

  React.useEffect(() => {
    if (!selectedLanguage) {
      setRows([]);
      return;
    }

    const editorRows = editorData?.localisationEditor.rows ?? [];
    const nextRows = buildRowsFromEditor(
      selectedFileName ?? "",
      selectedLanguage,
      editorRows
    );
    setRows((currentRows) => {
      if (currentRows.length === 0) {
        return nextRows;
      }

      const currentRowsById = new Map(currentRows.map((row) => [row.id, row]));
      return nextRows.map((row) => {
        const existingRow = currentRowsById.get(row.id);
        if (!existingRow || (!existingRow.isDirty && !existingRow.isSaving)) {
          return row;
        }

        return {
          ...row,
          text: existingRow.text,
          approved: existingRow.approved,
          isDirty: existingRow.isDirty,
          isSaving: existingRow.isSaving,
          isEditing: existingRow.isEditing,
        };
      });
    });
  }, [editorData, selectedFileName, selectedLanguage]);

  const isAnyTranslating = React.useMemo(
    () => rows.some((row) => row.isTranslating),
    [rows]
  );

  React.useEffect(() => {
    if (!selectedFileName || !selectedLanguage || !isAnyTranslating) {
      return;
    }

    const interval = window.setInterval(() => {
      void refetchEditor();
    }, 2000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isAnyTranslating, refetchEditor, selectedFileName, selectedLanguage]);

  const currentLanguage =
    languages.find((language) => language.code === selectedLanguage) ?? null;

  const orderedSections = React.useMemo(() => {
    const sectionsByName = new Map<string, SectionGroup>();

    rows.forEach((row, index) => {
      const existingSection = sectionsByName.get(row.section);
      if (existingSection) {
        existingSection.rows.push(row);
        existingSection.totalCount += 1;
        if (row.approved) {
          existingSection.approvedCount += 1;
        } else {
          existingSection.hasUnapproved = true;
        }
        return;
      }

      sectionsByName.set(row.section, {
        section: row.section,
        order: index,
        rows: [row],
        hasUnapproved: !row.approved,
        approvedCount: row.approved ? 1 : 0,
        totalCount: 1,
        approvedPercent: 0,
      });
    });

    return Array.from(sectionsByName.values())
      .sort((left, right) => {
        if (left.hasUnapproved !== right.hasUnapproved) {
          return left.hasUnapproved ? -1 : 1;
        }

        return left.order - right.order;
      })
      .map((section) => {
        const unapprovedRows: TranslationRow[] = [];
        const approvedRows: TranslationRow[] = [];

        section.rows.forEach((row) => {
          if (row.approved) {
            approvedRows.push(row);
          } else {
            unapprovedRows.push(row);
          }
        });

        return {
          ...section,
          rows: [...unapprovedRows, ...approvedRows],
          approvedPercent:
            section.totalCount === 0
              ? 0
              : Math.round(
                  (section.approvedCount / section.totalCount) * 100
                ),
        };
      });
  }, [rows]);

  const approvedCount = currentLanguage
    ? rows.filter((row) => row.approved).length
    : 0;
  const regenerateRow = rows.find((row) => row.id === regenerateRowId) ?? null;
  const isBusy = overviewLoading || editorLoading || isSwitchingLanguage;

  const closeRegenerateDialog = React.useCallback(() => {
    setRegenerateOpen(false);
    setRegenerateRowId(null);
    setRegenerateContext("");
  }, []);

  const setSelection = React.useCallback(
    (fileName: string | null, locale: string | null) => {
      const params = new URLSearchParams();
      if (fileName) {
        params.set("file", fileName);
      }
      if (locale) {
        params.set("locale", locale);
      }

      React.startTransition(() => {
        setSearchParams(params, { replace: true });
      });
    },
    [setSearchParams]
  );

  const handleFileChange = React.useCallback(
    (fileName: string) => {
      const nextFile = files.find((file) => file.name === fileName);
      const nextLocale =
        nextFile &&
        selectedLanguage &&
        nextFile.targetLocales.includes(selectedLanguage)
          ? selectedLanguage
          : nextFile?.targetLocales[0] ?? null;

      setSelection(fileName, nextLocale);
    },
    [files, selectedLanguage, setSelection]
  );

  const handleLanguageChange = React.useCallback(
    (languageCode: string) => {
      setSelection(selectedFileName, languageCode);
      closeRegenerateDialog();
      setIsSwitchingLanguage(true);

      if (languageTimerRef.current) {
        window.clearTimeout(languageTimerRef.current);
      }

      languageTimerRef.current = window.setTimeout(() => {
        setIsSwitchingLanguage(false);
      }, LANGUAGE_SWITCH_DELAY_MS);
    },
    [closeRegenerateDialog, selectedFileName, setSelection]
  );

  const persistTranslation = React.useCallback(
    async (
      rowId: string,
      rowKey: string,
      value: string,
      isApproved: boolean
    ) => {
      if (!selectedFileName || !selectedLanguage) {
        return;
      }

      setRows((currentRows) =>
        currentRows.map((row) =>
          row.id === rowId
            ? {
                ...row,
                text: value,
                approved: isApproved,
                isSaving: true,
              }
            : row
        )
      );

      try {
        await updateLocalisationTranslation({
          variables: {
            input: {
              fileName: selectedFileName,
              locale: selectedLanguage,
              key: rowKey,
              value,
              isApproved,
            },
          },
        });

        setRows((currentRows) =>
          currentRows.map((row) =>
            row.id === rowId
              ? {
                  ...row,
                  text: value,
                  approved: isApproved,
                  isDirty: false,
                  isSaving: false,
                }
              : row
          )
        );
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to save translation.";
        toast.error(message);
        setRows((currentRows) =>
          currentRows.map((row) =>
            row.id === rowId
              ? {
                  ...row,
                  isSaving: false,
                }
              : row
          )
        );
        await refetchEditor();
      }
    },
    [
      refetchEditor,
      selectedFileName,
      selectedLanguage,
      updateLocalisationTranslation,
    ]
  );

  const schedulePersist = React.useCallback(
    (rowId: string, rowKey: string, value: string, isApproved: boolean) => {
      const existingTimer = saveTimersRef.current[rowId];
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      saveTimersRef.current[rowId] = window.setTimeout(() => {
        delete saveTimersRef.current[rowId];
        void persistTranslation(rowId, rowKey, value, isApproved);
      }, 500);
    },
    [persistTranslation]
  );

  const clearApproval = React.useCallback((rowId: string) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId && row.approved
          ? {
              ...row,
              approved: false,
            }
          : row
      )
    );
  }, []);

  const stageTranslationValue = React.useCallback(
    (rowId: string, value: string, isApproved: boolean) => {
      setRows((currentRows) =>
        currentRows.map((row) =>
          stageTranslationRow(row, rowId, value, isApproved)
        )
      );
    },
    []
  );

  const handlePersist = React.useCallback(
    (rowId: string, rowKey: string, value: string, isApproved: boolean) => {
      stageTranslationValue(rowId, value, isApproved);
      schedulePersist(rowId, rowKey, value, isApproved);
    },
    [schedulePersist, stageTranslationValue]
  );

  const handlePersistNow = React.useCallback(
    (rowId: string, rowKey: string, value: string, isApproved: boolean) => {
      const existingTimer = saveTimersRef.current[rowId];
      if (existingTimer) {
        window.clearTimeout(existingTimer);
        delete saveTimersRef.current[rowId];
      }

      stageTranslationValue(rowId, value, isApproved);
      void persistTranslation(rowId, rowKey, value, isApproved);
    },
    [persistTranslation, stageTranslationValue]
  );

  const handleApprovedToggle = React.useCallback(
    async (
      rowId: string,
      rowKey: string,
      value: string,
      nextApproved: boolean
    ) => {
      const existingTimer = saveTimersRef.current[rowId];
      if (existingTimer) {
        window.clearTimeout(existingTimer);
        delete saveTimersRef.current[rowId];
      }

      stageTranslationValue(rowId, value, nextApproved);

      await persistTranslation(rowId, rowKey, value, nextApproved);
    },
    [persistTranslation, stageTranslationValue]
  );

  const openRegenerateDialog = React.useCallback((row: TranslationRow) => {
    setRegenerateRowId(row.id);
    setRegenerateContext("");
    setRegenerateOpen(true);
  }, []);

  const handleRegenerate = async () => {
    if (!regenerateRowId || !selectedFileName || !selectedLanguage) {
      return;
    }

    const rowId = regenerateRowId;
    const row = regenerateRow;
    const context = regenerateContext;
    const trimmedContext = context.trim();

    if (!row) {
      return;
    }

    setRegenerateOpen(false);
    setRegeneratingRowId(rowId);

    try {
      await queueLocalisationTranslation({
        variables: {
          input: {
            fileName: selectedFileName,
            locale: selectedLanguage,
            key: row.key,
            context: trimmedContext.length > 0 ? trimmedContext : null,
          },
        },
      });

      setRows((currentRows) =>
        currentRows.map((currentRow) =>
          currentRow.id === rowId
            ? {
                ...currentRow,
                approved: false,
                isTranslating: true,
              }
            : currentRow
        )
      );
      toast.success("Translation regeneration queued.");
      await refetchEditor();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to queue translation regeneration.";
      toast.error(message);
    } finally {
      setRegeneratingRowId(null);
      setRegenerateRowId(null);
      setRegenerateContext("");
    }
  };

  const pageActions = [
    {
      text: "selectors",
      element: (
        <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
          <div className="w-[180px] min-w-0">
            <Label htmlFor="localisation-file" className="sr-only">
              Localisation file
            </Label>
            <Select
              value={selectedFileName ?? undefined}
              onValueChange={handleFileChange}
              disabled={isBusy || files.length === 0}
            >
              <SelectTrigger
                id="localisation-file"
                className="w-full rounded-xl border-border/70 bg-muted/10"
              >
                <Upload className="size-4 text-muted-foreground" />
                <SelectValue placeholder="Select file" />
              </SelectTrigger>
              <SelectContent>
                {files.map((file) => (
                  <SelectItem key={file.name} value={file.name}>
                    {file.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[280px] min-w-0">
            <Label htmlFor="localisation-language" className="sr-only">
              Target language
            </Label>
            <Select
              value={selectedLanguage ?? undefined}
              onValueChange={handleLanguageChange}
            >
              <SelectTrigger
                id="localisation-language"
                className="w-full rounded-xl border-border/70 bg-muted/10"
                disabled={isBusy || !currentLanguage || languages.length === 0}
              >
                <Globe2 className="size-4 text-muted-foreground" />
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map((language) => (
                  <SelectItem key={language.code} value={language.code}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      text: "approved-pill",
      element: null,
    },
  ].filter((action) => action.element);

  const tableRows = React.useMemo(() => {
    if (!selectedLanguage) {
      return null;
    }

    return orderedSections.flatMap((section) => [
      <SectionHeaderRow
        key={`section-${section.section}`}
        section={section.section}
        approvedPercent={section.approvedPercent}
      />,
      ...section.rows.map((row) => (
        <TranslationTableRow
          key={row.id}
          row={row}
          isBusy={isBusy}
          isRegenerating={regeneratingRowId === row.id || row.isTranslating}
          onClearApproval={clearApproval}
          onSchedulePersist={handlePersist}
          onPersistNow={handlePersistNow}
          onApprovedToggle={handleApprovedToggle}
          onOpenRegenerateDialog={openRegenerateDialog}
        />
      )),
    ]);
  }, [
    clearApproval,
    handleApprovedToggle,
    handlePersist,
    handlePersistNow,
    isBusy,
    openRegenerateDialog,
    orderedSections,
    regeneratingRowId,
    selectedLanguage,
  ]);

  const subtitle = "Review and approve translations.";

  const showLoading =
    overviewLoading ||
    (Boolean(selectedFileName && selectedLanguage) && editorLoading);

  return (
    <PageTemplate
      title="Translation Management"
      subtitle={subtitle}
      activeItem="Admin"
      activeSubItem="Localisation"
      browserTitle="Localisation"
      actions={pageActions}
    >
      {showLoading ? (
        <LocalisationLoadingState />
      ) : overviewError ? (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardContent className="px-6 py-8 text-sm text-destructive">
            {overviewError.message}
          </CardContent>
        </Card>
      ) : files.length === 0 ? (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardContent className="px-6 py-8 text-sm text-muted-foreground">
            No localisation files are configured yet.
          </CardContent>
        </Card>
      ) : !selectedFile ? (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardContent className="px-6 py-8 text-sm text-muted-foreground">
            Select a localisation file to continue.
          </CardContent>
        </Card>
      ) : !currentLanguage ? (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardContent className="px-6 py-8 text-sm text-muted-foreground">
            No target locales are enabled for {selectedFile.name}.
          </CardContent>
        </Card>
      ) : editorError ? (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardContent className="px-6 py-8 text-sm text-destructive">
            {editorError.message}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden border-border/60 bg-card/70">
          <CardHeader className="border-b border-border/60">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg">
                {`English -> ${currentLanguage.label}`}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Globe2 className="size-4" />
                <span>{rows.length} strings</span>
                <span aria-hidden="true">|</span>
                <span>
                  {rows.length === 0
                    ? "0% approved (0/0)"
                    : `${Math.round((approvedCount / rows.length) * 100)}% approved (${approvedCount}/${rows.length})`}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0">
            {isSwitchingLanguage ? (
              <div className="space-y-3 px-6 py-5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="grid gap-3 md:grid-cols-[160px_1fr_1.2fr]"
                  >
                    <Skeleton className="h-10 rounded-md" />
                    <Skeleton className="h-10 rounded-md" />
                    <Skeleton className="h-10 rounded-md" />
                  </div>
                ))}
              </div>
            ) : (
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[20%] px-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Section
                    </TableHead>
                    <TableHead className="w-[40%] px-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      English
                    </TableHead>
                    <TableHead className="w-[40%] px-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {currentLanguage.label}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{tableRows}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={regenerateOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeRegenerateDialog();
          } else {
            setRegenerateOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate translation</DialogTitle>
            <DialogDescription>
              Provide additional context to improve the regenerated translation
              for{" "}
              <span className="font-medium text-foreground">
                {regenerateRow?.key ?? "selected string"}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="regenerate-context">Additional context</Label>
            <Textarea
              id="regenerate-context"
              value={regenerateContext}
              onChange={(event) => setRegenerateContext(event.target.value)}
              placeholder="For example: keep it short because this appears on a button."
              className="min-h-24"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeRegenerateDialog}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleRegenerate}>
              <RefreshCw className="size-4" />
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTemplate>
  );
};

export default LocalisationPage;
